import {
  isWhereGroup,
  type ReportSource,
  type WhereGroup,
  type WhereRule,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";

import type {
  ComponentFilter,
  ComponentValue,
  FilterExpr,
  FilterGroup,
} from "../types/rule";

/** Minimal rule condition row shape from GraphQL ``RuleDefinitionObject.conditions``. */
export type RuleConditionRow = {
  connector?: string | null;
  isNegated?: boolean | null;
  field?: string | null;
  operator?: string | null;
  valueType?: string | null;
  value?: string | null;
  children?: RuleConditionRow[] | null;
};

export function parseRuleConditionValue(
  condition: RuleConditionRow,
): ComponentValue {
  if (condition.valueType === "CONTEXT_PATH") {
    return { type: "variable", nodeId: "", path: condition.value ?? "" };
  }
  const raw = condition.value ?? "";
  if (raw === "true") return { type: "constant", value: true };
  if (raw === "false") return { type: "constant", value: false };
  if (raw.startsWith("{") || raw.startsWith("[")) {
    try {
      return { type: "constant", value: JSON.parse(raw) };
    } catch {
      // fall through
    }
  }
  const asNumber = Number(raw);
  if (raw !== "" && Number.isFinite(asNumber)) {
    return { type: "constant", value: asNumber };
  }
  return { type: "constant", value: raw };
}

export function ruleConditionToFilterExpr(
  condition: RuleConditionRow,
): FilterExpr {
  if (condition.connector === "LEAF") {
    const leaf: ComponentFilter = {
      field: condition.field ?? "",
      operator: normalizeConditionOperator(condition.operator),
      value: parseRuleConditionValue(condition),
      ...(condition.isNegated ? { not: true } : {}),
    };
    return leaf;
  }

  const children = (condition.children ?? []).map(ruleConditionToFilterExpr);
  const group: FilterGroup = {
    type: condition.connector === "OR" ? "or" : "and",
    children,
    ...(condition.isNegated ? { not: true } : {}),
  };
  return group;
}

export function ruleConditionsToFilterGroup(
  conditions: RuleConditionRow[],
): FilterGroup {
  if (conditions.length === 0) {
    return { type: "and", children: [] };
  }
  if (conditions.length === 1) {
    const expr = ruleConditionToFilterExpr(conditions[0]);
    if (isFilterGroup(expr)) return expr;
    return { type: "and", children: [expr] };
  }
  return {
    type: "and",
    children: conditions.map(ruleConditionToFilterExpr),
  };
}

/** Map GraphQL / legacy operator strings to filter-chip operator keys. */
export function normalizeConditionOperator(
  operator: string | null | undefined,
): string {
  if (!operator) return "";
  const graphQlToChip: Record<string, string> = {
    IN_LIST: "inList",
    NOT_IN_LIST: "notInList",
    I_EXACT: "iExact",
    I_CONTAINS: "iContains",
    NOT_CONTAINS_CAMEL: "notContains",
    STARTS_WITH_CAMEL: "startsWith",
    ENDS_WITH_CAMEL: "endsWith",
    IS_NULL_CAMEL: "isNull",
    IS_NOT_NULL_CAMEL: "isNotNull",
  };
  if (graphQlToChip[operator]) return graphQlToChip[operator];
  if (operator === "in_list") return "inList";
  if (operator === "not_in_list") return "notInList";
  if (operator === "is_null") return "isNull";
  if (operator === "is_not_null") return "isNotNull";
  if (operator === "i_exact") return "iExact";
  if (operator === "i_contains") return "iContains";
  if (/^[A-Z][A-Z0-9_]*$/.test(operator)) {
    const lower = operator.toLowerCase();
    if (lower === "is_null") return "isNull";
    if (lower === "is_not_null") return "isNotNull";
    return lower;
  }
  return operator;
}

export function isMultiselectOperator(
  operator: string | null | undefined,
): boolean {
  const normalized = normalizeConditionOperator(operator);
  return normalized === "inList" || normalized === "notInList";
}

export function isFilterGroup(expr: FilterExpr): expr is FilterGroup {
  return "type" in expr && (expr.type === "and" || expr.type === "or");
}

function filterExprToPortable(expr: FilterExpr): unknown {
  if (isFilterGroup(expr)) {
    const payload: Record<string, unknown> = {
      type: expr.type,
      children: expr.children.map(filterExprToPortable),
    };
    if (expr.not) payload.not = true;
    return payload;
  }
  const leaf = expr as ComponentFilter;
  const payload: Record<string, unknown> = {
    field: leaf.field,
    operator: normalizeConditionOperator(leaf.operator),
  };
  if (leaf.value?.type === "constant") {
    const constantValue = leaf.value.value;
    if (Array.isArray(constantValue)) {
      payload.value = JSON.stringify(constantValue);
    } else if (constantValue != null && typeof constantValue === "object") {
      payload.value = JSON.stringify(constantValue);
    } else {
      payload.value = constantValue;
    }
  } else if (leaf.value?.type === "variable") {
    payload.value = { type: "context_path", path: leaf.value.path };
  }
  if (leaf.not) payload.not = true;
  return payload;
}

export function serializeFilterGroup(group: FilterGroup): unknown {
  if (group.type === "and" && !group.not) {
    return group.children.map(filterExprToPortable);
  }
  return filterExprToPortable(group);
}

/**
 * What a rule's "When" can ask of the bank transaction that sets it off, as the
 * report widgets' where editor lists a source's columns: the fields the server
 * matches a new transaction on. A bank account is compared as the account
 * itself - kept by its identifier - not by its name.
 */
export const RULE_WHEN_SOURCE: ReportSource = {
  name: "transactions",
  label: "Bank transactions",
  presentations: [],
  filters: [],
  isSummarized: false,
  fields: [
    {
      name: "amount",
      label: "Amount",
      kind: "money",
      holds: "money",
      isGroupable: false,
      isMeasurable: false,
      takesInterval: false,
    },
    {
      name: "description",
      label: "Description",
      kind: "text",
      holds: "text",
      isGroupable: false,
      isMeasurable: false,
      takesInterval: false,
    },
    {
      name: "merchant_name",
      label: "Merchant",
      kind: "text",
      holds: "text",
      isGroupable: false,
      isMeasurable: false,
      takesInterval: false,
    },
    {
      name: "financial_account",
      label: "Bank account",
      kind: "reference",
      holds: "reference",
      typename: "FinancialAccountObject",
      isGroupable: false,
      isMeasurable: false,
      takesInterval: false,
    },
    {
      // Empty until the payment is booked: what a rule for payments still to
      // book - a business's default one - asks of it.
      name: "journal_entry",
      label: "Journal entry",
      kind: "reference",
      holds: "reference",
      typename: "JournalEntryObject",
      isGroupable: false,
      isMeasurable: false,
      takesInterval: false,
    },
  ],
};

/** A rule's comparisons, by the where editor's name for them: and those that say "not" themselves. */
const WHERE_OPERATOR_OF: Record<
  string,
  { operator: string; isNegated?: boolean }
> = {
  eq: { operator: "eq" },
  equals: { operator: "eq" },
  exact: { operator: "eq" },
  iExact: { operator: "eq" },
  ne: { operator: "eq", isNegated: true },
  not_equals: { operator: "eq", isNegated: true },
  contains: { operator: "contains" },
  iContains: { operator: "contains" },
  notContains: { operator: "contains", isNegated: true },
  not_contains: { operator: "contains", isNegated: true },
  startsWith: { operator: "starts_with" },
  starts_with: { operator: "starts_with" },
  endsWith: { operator: "ends_with" },
  ends_with: { operator: "ends_with" },
  isNull: { operator: "is_null" },
  is_null: { operator: "is_null" },
  isNotNull: { operator: "is_null", isNegated: true },
  is_not_null: { operator: "is_null", isNegated: true },
};

/**
 * A rule's conditions as the report widgets' where editor writes them - the
 * same tree in its words. One of several records ("is one of") is each of
 * them in a group of its own; any other value than a written one is left
 * empty for the editor.
 */
export function filterGroupToWhereGroup(group: FilterGroup): WhereGroup {
  return {
    connector: group.type === "or" ? "any" : "all",
    isNegated: group.not || undefined,
    terms: group.children.map((child): WhereRule | WhereGroup => {
      if (isFilterGroup(child)) return filterGroupToWhereGroup(child);
      const operator = normalizeConditionOperator(child.operator);
      const value = child.value?.type === "constant" ? child.value.value : null;
      if (operator === "inList" || operator === "notInList") {
        const listed = Array.isArray(value)
          ? value
          : value == null
            ? []
            : [value];
        const isNegated = (operator === "notInList") !== Boolean(child.not);
        // Not one of them is none of them: each "is not", all together.
        return {
          connector: isNegated ? "all" : "any",
          terms: listed.map((one) => ({
            field: child.field,
            operator: "eq",
            value: one,
            isNegated: isNegated || undefined,
          })),
        };
      }
      const said = WHERE_OPERATOR_OF[operator] ?? { operator };
      return {
        field: child.field,
        operator: said.operator,
        value,
        isNegated: Boolean(said.isNegated) !== Boolean(child.not) || undefined,
      };
    }),
  };
}

/**
 * What the where editor wrote, as a rule keeps it: in the comparisons a
 * rule's own fields use - words compared case by case - with "not" as the
 * condition's own flag.
 */
export function whereGroupToFilterGroup(group: WhereGroup): FilterGroup {
  return {
    type: group.connector === "any" ? "or" : "and",
    ...(group.isNegated ? { not: true } : {}),
    children: group.terms.map((term): FilterExpr => {
      if (isWhereGroup(term)) return whereGroupToFilterGroup(term);
      const isWords =
        RULE_WHEN_SOURCE.fields.find((field) => field.name === term.field)
          ?.kind === "text";
      const operator =
        term.operator === "is_null"
          ? "isNull"
          : term.operator === "starts_with"
            ? "startsWith"
            : term.operator === "ends_with"
              ? "endsWith"
              : isWords && term.operator === "eq"
                ? "iExact"
                : isWords && term.operator === "contains"
                  ? "iContains"
                  : term.operator;
      return {
        field: term.field,
        operator,
        value: { type: "constant", value: term.value ?? "" },
        ...(term.isNegated ? { not: true } : {}),
      };
    }),
  };
}
